import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  Box,
  ListItemText,
  Divider,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConfirmationDialog from '../../../layouts/shared/ConfirmationDialog';
import { Colors } from '../../../landingPage/style/theme';


const CustomizedPlanBox = (props) => {
  const [data, setData] = useState([]);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [itemIdToDelete, setItemIdToDelete] = useState(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/getCustomizedPlansByUserId?userId=${localStorage.getItem(
        'userId'
      )}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.success && responseData.data.length > 0) {
        setData(responseData.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBuyPlan = (plan, e) => {
    e.preventDefault();
    if (plan._id) {
      props.openPurchaseCustomizeForm(plan._id);
      const encodedSubscriptionId = encodeURIComponent(plan._id);
      const encodedSubscriptionName = 'Customize Plan';
      const encodedSubscriptionAmount = encodeURIComponent(plan.SubscriptionAmount);
      navigate(
        `/dashboard/subscription/${encodedSubscriptionId}/${encodedSubscriptionName}/${encodedSubscriptionAmount}`
      );
    } else {
      toast(`Plan with ID ${plan._id} not getting response`);
    }
  };

  const handleDeletePlan = (plan, e) => {
    e.preventDefault();
    setItemIdToDelete(plan._id);
    setIsConfirmationDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsConfirmationDialogOpen(false);
    setIsLoading(true);
    try {
      const cleanToken = token.replace(/"/g, '');
      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      };
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/deleteCustomPlanByUser`,
        { customPlanId: itemIdToDelete },
        config
      );
      if (response.data.success === true) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response.data.message);
      console.error('Error deleting address:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmationDialogOpen(false);
    setItemIdToDelete(null);
  };

  CustomizedPlanBox.propTypes = {
    openPurchaseCustomizeForm: PropTypes.func.isRequired,
  };

  return (
    <>
      <Typography variant="h6" sx={{ mt: 2, mb: 2, color: Colors.primary }}>
        Customize Plans
      </Typography>
      <Grid container spacing={3}>
        {isLoading ? (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Grid>
        ) : (
          <>
            {data.length === 0 ? (
              <Grid container>
                <Grid item xs={12} >
                  <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
                    No Customize Plans
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              data.map((plan, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: Colors.secondary,
                      borderWidth: 1,
                      borderRadius: '8px',
                      transition: '0.3s',
                      '&:hover': {
                        boxShadow: 3,
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                        Pricing Plan
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ textAlign: 'center', mt: 2, color: Colors.secondary }}
                      >
                        {plan.SubscriptionAmount || '__'} / {plan.subscriptionDays || '__'} Days
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
                        Created Date: {new Date(plan.createdAt).toLocaleDateString()}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <List>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleIcon sx={{ color: Colors.primary }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Subscription Days: ${plan.subscriptionDays} Days`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleIcon sx={{ color: Colors.primary }} />
                          </ListItemIcon>
                          <ListItemText primary={`Number of Hit's: ${plan.numberOfHits || 0}`} />
                        </ListItem>
                      </List>
                      <Divider sx={{ my: 2 }} />
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 2,
                          mt: 2,
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            backgroundColor: Colors.primary,
                            '&:hover': {
                              backgroundColor: Colors.secondary,
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBuyPlan(plan, e);
                          }}
                        >
                          Buy Plan
                        </Button>
                        <Button
                          variant="outlined"
                          color='error'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePlan(plan, e);
                          }}
                        >
                          Delete Plan
                        </Button>
                      </Box>

                    </CardContent>
                    <ConfirmationDialog
                      isOpen={isConfirmationDialogOpen}
                      onClose={handleCancelDelete}
                      onConfirm={handleConfirmDelete}
                      message="Are you sure you want to delete customized plan?"
                    />
                  </Card>
                </Grid>
              ))
            )}
          </>
        )}
      </Grid>
    </>
  );

};

export default CustomizedPlanBox;
